import type { FC, ReactElement } from 'react';

import type { UploaderState } from '@iconik-io/uploader/src/state';

import TooltipTrigger from '../../components/tooltip_trigger/tooltip_trigger';
import VARIANTS from '../../constants/variants';
import {
  canClearUpload,
  isUploadedFile,
} from '../../ducks/uploader/upload_state';
import Button from '../../ui/button/button';
import GearBoxActions from '../../ui/gearbox_actions/gearbox_actions';
import IconButton from '../../ui/icon_button/icon_button';
import ButtonsSeparator from '../../ui/page_block_header/buttons_separator';
import PageBlockHeader from '../../ui/page_block_header/page_block_header';
import UploaderContextMenu from './context_menu_panel';

import styles from './uploads_panel_widget.module.css';

type Props = {
  items: UploaderState[];
  title: string | ReactElement;
  onClose: () => void;
  onMinimize: () => void;
  onSeeNew: () => void;
  onClear: () => void;
  onExpand?: () => void;
  fullscreen?: boolean;
  expanded?: boolean;
  isSmallScreen?: boolean;
  isContextMenuEmpty?: boolean;
  canSearch?: boolean;
};

const UploadsHeader: FC<Props> = (props) => {
  const {
    title,
    onExpand,
    onClose,
    onMinimize,
    onSeeNew,
    onClear,
    items,
    fullscreen,
    isSmallScreen,
    expanded,
    isContextMenuEmpty,
    canSearch,
  } = props;

  const hasNewAssets = items.some(isUploadedFile);

  const canClear = items.some(canClearUpload);

  const actionElements = [];

  if (hasNewAssets && canSearch) {
    if (fullscreen && !isSmallScreen) {
      actionElements.push(
        <Button
          key="new"
          variant={VARIANTS.WHITE}
          iconName="display_show"
          onClick={onSeeNew}
          wideBasis={false}
          className={styles.clear}
          aria={{ label: 'See new' }}
        >
          See new
        </Button>,
      );
    } else {
      actionElements.push(
        <TooltipTrigger
          key="new"
          tooltipText="See new"
          placement={'top'}
          component={IconButton}
          componentProps={{
            variant: VARIANTS.WHITE,
            aria: { label: 'See new' },
            iconName: 'display_show',
            onClick: onSeeNew,
          }}
        />,
      );
    }
  }
  if (canClear) {
    if (fullscreen && !isSmallScreen) {
      actionElements.push(
        <Button
          key="clear"
          variant={VARIANTS.WHITE}
          iconName="broom"
          onClick={onClear}
          wideBasis={false}
          className={styles.clear}
          aria={{ label: 'Clear Queue' }}
        >
          Clear Queue
        </Button>,
      );
    } else {
      actionElements.push(
        <TooltipTrigger
          key="clear"
          tooltipText="Clear Queue"
          placement={'top'}
          component={IconButton}
          componentProps={{
            variant: VARIANTS.WHITE,
            aria: { label: 'Clear Queue' },
            iconName: 'broom',
            onClick: onClear,
          }}
        />,
      );
    }
  }
  if (!isContextMenuEmpty) {
    actionElements.push(
      <GearBoxActions
        key="menu"
        contextMenu={UploaderContextMenu}
        contextItems={[]}
        triggerElement={<IconButton iconName="menu_context" variant="white" />}
      />,
    );
  }
  if (!fullscreen || (fullscreen && expanded)) {
    if (!isContextMenuEmpty || canClear) {
      actionElements.push(<ButtonsSeparator key="separator" />);
    }
  }

  if (!fullscreen && onExpand && !isSmallScreen) {
    actionElements.push(
      <IconButton
        key="expand"
        iconName="fit_viewport"
        onClick={onExpand}
        variant="white"
      />,
    );
  }

  if (fullscreen && expanded) {
    actionElements.push(
      <IconButton
        key="minimize"
        variant="white"
        iconName="player_view_normal"
        onClick={onMinimize}
      />,
    );
  }

  if (!fullscreen || (fullscreen && expanded)) {
    actionElements.push(
      <IconButton
        key="close"
        variant="white"
        iconName="close"
        onClick={onClose}
      />,
    );
  }

  return (
    <PageBlockHeader
      className={fullscreen ? styles.fullscreen : undefined}
      title={title}
      actionElements={actionElements}
      hasUADIcons
    />
  );
};

export default UploadsHeader;
